/**
 * A period or a day in the reader's words (production audit 4): «settimana 41 del 2026», «23 set 2026»,
 * not `2026-W41` and `2026-09-23`. The identifiers are the platform's and stay in the links, the files and
 * the filters; a screen's sentence says them as a planner reads them.
 *
 *   `YYYY-Www`        a retail (ISO) week   → «settimana 41 del 2026»
 *   `YYYY-MM`         a calendar month      → «settembre 2026»
 *   `YYYY-MM-DD`      a day                 → «23 set 2026»
 *   anything else     itself — a period this build cannot read is shown as the platform wrote it,
 *                     never guessed at and never dropped
 *
 * Pure over the translate function and the language. The weeks are words from the catalogue
 * (`idWords.week`), so a fourth locale words them by adding that one key.
 */

import type { TFunction } from 'i18next';

import { formatDate } from '../lib/format';

type Say = TFunction;

const WEEK = /^(\d{4})-W(\d{2})$/;
const MONTH = /^(\d{4})-(\d{2})$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A calendar day (`YYYY-MM-DD`) as the locale's medium date, in UTC (a day has no time zone); else itself. */
export function dayWords(value: string, language: string): string {
  if (!DAY.test(value)) return value;
  const at = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(at.getTime()) ? value : formatDate(at.toISOString(), language, 'UTC');
}

/** A period key in words — see the module note. */
export function periodWords(value: string, t: Say, language: string): string {
  const week = WEEK.exec(value);
  if (week !== null) {
    const number = Number(week[2]);
    // Strings, not numbers: a number would be grouped by the ICU formatter («2.026»).
    if (number >= 1 && number <= 53) return t('idWords.week', { week: String(number), year: week[1] as string });
    return value;
  }
  const month = MONTH.exec(value);
  if (month !== null) {
    const index = Number(month[2]);
    if (index < 1 || index > 12) return value;
    return new Intl.DateTimeFormat(language, { month: 'long', year: 'numeric', timeZone: 'UTC' })
      .format(new Date(Date.UTC(Number(month[1]), index - 1, 1)));
  }
  return dayWords(value, language);
}
